// path: src/utils/publicProfile.js
// Friend-readable snapshot shape (Phase A writes; Phase B/C consume).

import { formatGid, isValidGid } from "./gid";
import { migrateCollectionIds, migrateLegacySpecialId } from "./cardCatalog";
import { normalizeEndlessStats } from "./achievements";
import { normalizeStoryProgress } from "./storyProgress";
import {
    getEquippedCosmetics,
    normalizeCosmeticsState,
} from "./cosmetics";

const normalizeIdList = (ids = []) => {
    const seen = new Set();
    const out = [];

    (Array.isArray(ids) ? ids : []).forEach((id) => {
        if (id === null || id === undefined || id === "") {
            return;
        }

        const n = Number(id);
        const value = Number.isFinite(n) ? n : id;
        if (value === null || value === undefined || seen.has(value)) {
            return;
        }
        seen.add(value);
        out.push(value);
    });

    return out;
};

export function buildPublicProfile({
    uid,
    gid,
    username = "",
    profileImageId = null,
    gamesPlayed = 0,
    topScore = 0,
    collectionUnlocked = [],
    collectionFoilUnlocked = [],
    endlessStats = null,
    storyProgress = null,
    cosmetics = null,
} = {}) {
    if (!uid) {
        return null;
    }

    const normalizedGid = isValidGid(gid) ? Number(gid) : null;
    const unlocked = migrateCollectionIds(normalizeIdList(collectionUnlocked));
    const foils = migrateCollectionIds(normalizeIdList(collectionFoilUnlocked));
    const endless = normalizeEndlessStats(endlessStats);
    const story = normalizeStoryProgress(storyProgress);
    const cosmeticsState = normalizeCosmeticsState(cosmetics);
    const equipped = getEquippedCosmetics(cosmeticsState);

    return {
        uid: String(uid),
        gid: normalizedGid,
        gidDisplay: normalizedGid ? formatGid(normalizedGid) : "",
        username: typeof username === "string" ? username : "",
        profileImageId:
            profileImageId !== null && profileImageId !== undefined
                ? migrateLegacySpecialId(
                      Number.isFinite(Number(profileImageId))
                          ? Number(profileImageId)
                          : profileImageId
                  )
                : null,
        gamesPlayed: Math.max(0, Math.floor(Number(gamesPlayed) || 0)),
        topScore: Math.max(0, Math.floor(Number(topScore) || 0)),
        cardsOwned: unlocked.length,
        foilsFound: foils.length,
        collectionUnlocked: unlocked,
        collectionFoilUnlocked: foils,
        endlessStats: endless,
        storyProgress: story,
        cosmetics: cosmeticsState,
        equippedTitle: equipped.title?.name || "",
        equippedAvatarFrameId: cosmeticsState.equipped.avatarFrameId,
        equippedMapBadgeId: cosmeticsState.equipped.mapBadgeId,
        highestLevel: Math.max(
            0,
            ...Object.values(endless.bestLevelByBoardSize || {}).map(
                (value) => Number(value) || 0
            )
        ),
        longestStreak: Number(endless.bestPerfectStreak) || 0,
        updatedAtClient: Date.now(),
    };
}

/**
 * Normalize a Firestore publicProfiles/{uid} document for friend viewing.
 */
export function parsePublicProfileDoc(uid, data = {}) {
    if (!uid || !data || typeof data !== "object") {
        return null;
    }

    return buildPublicProfile({
        uid,
        gid: data.gid,
        username: data.username,
        profileImageId: data.profileImageId,
        gamesPlayed: data.gamesPlayed,
        topScore: data.topScore,
        collectionUnlocked: data.collectionUnlocked,
        collectionFoilUnlocked: data.collectionFoilUnlocked,
        endlessStats: data.endlessStats,
        storyProgress: data.storyProgress,
        cosmetics: data.cosmetics,
    });
}
